import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function ConfigurationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const [maintenanceDuration, setMaintenanceDuration] = useState(1);
  const [maintenanceIllimitee, setMaintenanceIllimitee] = useState(false);

  // Turnstile diagnostics
  const [turnstileCheckResult, setTurnstileCheckResult] = useState<any>(null);
  const [turnstileChecking, setTurnstileChecking] = useState(false);
  const [cloudflareAccountId, setCloudflareAccountId] = useState('');
  const [addingLocalhost, setAddingLocalhost] = useState(false);
  const [localhostAddResult, setLocalhostAddResult] = useState<any>(null);
  const [turnstileAccountId, setTurnstileAccountId] = useState('');

  const [generalConfig, setGeneralConfig] = useState({
    clinicName: 'Clinique Médicale',
    clinicEmail: 'contact@clinique.com',
    clinicPhone: '+33 1 23 45 67 89',
    clinicAddress: '123 Rue de la Santé, 75000 Paris',
    workingHours: '8h00 - 18h00',
    workingDays: 'Lundi - Vendredi',
  });

  const [rdvConfig, setRdvConfig] = useState({
    dureeConsultation: 30,
    delaiAnnulation: 24,
    maxRdvParJour: 20,
    rappelAvant: 24,
    rendez_vous_urgents_active: true,
  });

  const [securityConfig, setSecurityConfig] = useState({
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    requireStrongPassword: true,
    requireEmailVerification: true,
    requirePhoneVerification: true,
    verification_forced_user_ids: [] as string[],
  });

  // Vérification forcée par utilisateur
  const [verificationForcedUsers, setVerificationForcedUsers] = useState<any[]>([]);
  const [verificationSearchTerm, setVerificationSearchTerm] = useState('');
  const [verificationSearchResults, setVerificationSearchResults] = useState<any[]>([]);
  const [verificationSearchLoading, setVerificationSearchLoading] = useState(false);

  const [restoreFile, setRestoreFile] = useState<string>('');
  const [backups, setBackups] = useState<any[]>([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState('');
  const [backupCronStatus, setBackupCronStatus] = useState<{ configured: boolean; activated_at: string | null; cron_job_exists: boolean; checking: boolean }>({ configured: false, activated_at: null, cron_job_exists: false, checking: true });

  // Visioconférence
  const [visioPermissions, setVisioPermissions] = useState<any[]>([]);
  const [visioSearchTerm, setVisioSearchTerm] = useState('');
  const [visioSearchResults, setVisioSearchResults] = useState<any[]>([]);
  const [visioLoading, setVisioLoading] = useState(false);

  useEffect(() => {
    checkAdmin();
    checkMaintenanceStatus();
    loadConfigurations();
    checkBackupCronStatus();
  }, [user]);

  useEffect(() => {
    if (!loading) {
      loadBackups();
    }
  }, [loading]);

  // Helper: récupère le token d'accès actuel
  const getAccessToken = async (): Promise<string | null> => {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  };

  const loadBackups = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/database-backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'list' }),
      });

      const result = await response.json();
      if (result.backups) {
        setBackups(result.backups);
      }
    } catch (err) {
      console.error('Erreur chargement backups:', err);
    }
  };

  const checkBackupCronStatus = async () => {
    setBackupCronStatus(prev => ({ ...prev, checking: true }));
    try {
      const token = await getAccessToken();
      if (!token) {
        setBackupCronStatus({ configured: false, activated_at: null, cron_job_exists: false, checking: false });
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/setup-backup-cron`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'check' }),
      });

      const result = await response.json();
      console.log('checkBackupCronStatus result:', result);
      if (!response.ok) {
        console.error('checkBackupCronStatus HTTP error:', response.status, result);
        setErrorMessage(result.error || `Erreur ${response.status} lors de la vérification du cron`);
        setTimeout(() => setErrorMessage(''), 5000);
      }
      setBackupCronStatus({
        configured: result.configured || false,
        activated_at: result.activated_at || null,
        cron_job_exists: result.cron_job_exists || false,
        checking: false,
      });
    } catch (err: any) {
      console.error('checkBackupCronStatus exception:', err);
      setErrorMessage('Erreur réseau lors de la vérification du cron: ' + (err.message || 'inconnue'));
      setTimeout(() => setErrorMessage(''), 5000);
      setBackupCronStatus({ configured: false, activated_at: null, cron_job_exists: false, checking: false });
    }
  };

  const checkAdmin = async () => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    const { data, error } = await supabase
      .from('super_admins')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error || !data) {
      navigate('/', { replace: true });
      return;
    }

    setLoading(false);
  };

  const loadConfigurations = async () => {
    try {
      // Charger la configuration générale
      const { data: generalData } = await supabase
        .from('system_config')
        .select('*')
        .eq('config_key', 'general')
        .single();

      if (generalData) {
        setGeneralConfig(generalData.config_value);
      }

      // Charger la configuration des rendez-vous
      const { data: rdvData } = await supabase
        .from('system_config')
        .select('*')
        .eq('config_key', 'rdv')
        .single();

      if (rdvData) {
        setRdvConfig(rdvData.config_value);
      }

      // Charger la configuration de sécurité
      const { data: securityData } = await supabase
        .from('system_config')
        .select('*')
        .eq('config_key', 'security')
        .single();

      if (securityData) {
        setSecurityConfig({
          sessionTimeout: 30,
          maxLoginAttempts: 5,
          passwordMinLength: 8,
          requireStrongPassword: true,
          requireEmailVerification: true,
          requirePhoneVerification: true,
          verification_forced_user_ids: [] as string[],
          ...securityData.config_value,
        });
        // Charger les infos des utilisateurs avec vérification forcée
        loadForcedUsersDetails(securityData.config_value?.verification_forced_user_ids || []);
      }
    } catch (error) {
      console.error('Erreur chargement configurations:', error);
    }
  };

  const checkMaintenanceStatus = async () => {
    const { data, error } = await supabase
      .from('maintenance_mode')
      .select('*')
      .eq('id', '00000000-0000-0000-0000-000000000001')
      .single();

    if (error) {
      console.error('Erreur vérification maintenance:', error);
      return;
    }

    if (data) {
      setMaintenanceActive(data.is_active);
    }
  };

  // === Turnstile Diagnostic ===
  const handleCheckTurnstile = async (manualAccountId?: string) => {
    setTurnstileChecking(true);
    setTurnstileCheckResult(null);
    try {
      const token = await getAccessToken();
      if (!token) {
        setTurnstileCheckResult({ status: 'error', reason: 'Non authentifié' });
        return;
      }

      const body: any = {};
      if (manualAccountId) {
        body.accountId = manualAccountId;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/fetch-turnstile-widget-info`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
        }
      );

      const result = await response.json();
      setTurnstileCheckResult(result);
      if (result.status === 'ok' && result.account?.id) {
        setTurnstileAccountId(result.account.id);
      }
    } catch (err: any) {
      setTurnstileCheckResult({ status: 'error', reason: err.message || 'Erreur réseau' });
    } finally {
      setTurnstileChecking(false);
    }
  };

  const handleAddLocalhost = async () => {
    setAddingLocalhost(true);
    setLocalhostAddResult(null);
    try {
      const token = await getAccessToken();
      if (!token) {
        setLocalhostAddResult({ status: 'error', reason: 'Non authentifi\u00e9' });
        return;
      }

      const body: any = {};
      if (turnstileAccountId) {
        body.accountId = turnstileAccountId;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-turnstile-domains`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      const result = await response.json();
      setLocalhostAddResult(result);

      if (result.status === 'ok') {
        handleCheckTurnstile(turnstileAccountId);
      }
    } catch (err: any) {
      setLocalhostAddResult({ status: 'error', reason: err.message || 'Erreur r\u00e9seau' });
    } finally {
      setAddingLocalhost(false);
    }
  };

  const handleActivateMaintenance = async () => {
    setLoading(true);
    try {
      // Étape 1 : Déconnecter TOUS les utilisateurs non-admin AVANT d'activer la maintenance
      try {
        const token = await getAccessToken();
        if (token) {
          const logoutResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/force-logout-non-admins`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({}),
          });
          const logoutResult = await logoutResponse.json();
          if (logoutResult.success) {
            console.log(`Force logout: ${logoutResult.loggedOut} utilisateurs déconnectés`);
          } else {
            console.warn('Force logout partiel:', logoutResult.error);
          }
        }
      } catch (logoutErr) {
        console.error('Erreur force logout:', logoutErr);
        // On continue quand même - la maintenance sera activée
      }

      // Étape 2 : Activer le mode maintenance
      const endTime = maintenanceIllimitee ? null : (() => {
        const et = new Date();
        et.setHours(et.getHours() + maintenanceDuration);
        return et.toISOString();
      })();

      const { error } = await supabase
        .from('maintenance_mode')
        .update({
          is_active: true,
          start_time: new Date().toISOString(),
          end_time: endTime,
          message: 'Le site est actuellement en maintenance. Nous serons de retour bientôt.',
          updated_at: new Date().toISOString()
        })
        .eq('id', '00000000-0000-0000-0000-000000000001');

      if (error) {
        setErrorMessage('Erreur lors de l\'activation de la maintenance');
        setTimeout(() => setErrorMessage(''), 3000);
        setLoading(false);
        return;
      }

      setMaintenanceActive(true);
      const msg = maintenanceIllimitee ? 'Mode maintenance activé (durée illimitée) — Tous les utilisateurs ont été déconnectés' : `Mode maintenance activé pour ${maintenanceDuration}h — Tous les utilisateurs ont été déconnectés`;
      setSuccessMessage(msg);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setErrorMessage('Erreur inattendue');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateMaintenance = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('maintenance_mode')
        .update({
          is_active: false,
          start_time: null,
          end_time: null,
          message: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', '00000000-0000-0000-0000-000000000001');

      if (error) {
        setErrorMessage('Erreur lors de la désactivation de la maintenance');
        setTimeout(() => setErrorMessage(''), 3000);
        setLoading(false);
        return;
      }

      setMaintenanceActive(false);
      setSuccessMessage('Mode maintenance désactivé');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setErrorMessage('Erreur inattendue');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Non authentifié');

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-system-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          configType: 'general',
          configData: generalConfig,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la sauvegarde');
      }

      setSuccessMessage('Configuration générale enregistrée avec succès !');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setErrorMessage(error.message || 'Erreur lors de la sauvegarde');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRdv = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Non authentifié');

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-system-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          configType: 'rdv',
          configData: rdvConfig,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la sauvegarde');
      }

      setSuccessMessage('Configuration des rendez-vous enregistrée avec succès !');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setErrorMessage(error.message || 'Erreur lors de la sauvegarde');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSecurity = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Non authentifié');

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-system-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          configType: 'security',
          configData: securityConfig,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la sauvegarde');
      }

      setSuccessMessage('Configuration de sécurité enregistrée avec succès !');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setErrorMessage(error.message || 'Erreur lors de la sauvegarde');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupBackupCron = async () => {
    setBackupLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Non authentifié');

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/setup-backup-cron`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      const result = await response.json();
      console.log('handleSetupBackupCron result:', result);

      if (!response.ok || result.error) {
        throw new Error(result.error || result.suggestion || 'Erreur lors de la configuration du cron');
      }

      if (result.suggestion) {
        setSuccessMessage(result.message + ' — ' + result.suggestion);
      } else {
        setSuccessMessage(result.message || 'Cron de sauvegarde configuré avec succès !');
      }
      // Rafraîchir le statut après configuration
      await checkBackupCronStatus();
      setTimeout(() => setSuccessMessage(''), 8000);
    } catch (error: any) {
      console.error('handleSetupBackupCron error:', error);
      setErrorMessage(error.message || 'Erreur lors de la configuration du cron');
      setTimeout(() => setErrorMessage(''), 8000);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Non authentifié');

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/database-backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'backup' }),
      });

      const result = await response.json();

      if (!response.ok || result.error) {
        throw new Error(result.error || 'Erreur lors de la sauvegarde');
      }

      setSuccessMessage(`Sauvegarde réussie ! ${result.tablesExported} tables exportées dans ${result.fileName}`);
      setTimeout(() => setSuccessMessage(''), 5000);

      if (result.backups) {
        setBackups(result.backups);
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Erreur lors de la sauvegarde');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreConfirm = () => {
    if (!restoreTarget) {
      setErrorMessage('Veuillez sélectionner un fichier de sauvegarde');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    setShowRestoreConfirm(true);
  };

  const handleRestoreExecute = async () => {
    setShowRestoreConfirm(false);
    setBackupLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Non authentifié');

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/database-backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'restore', fileName: restoreTarget }),
      });

      const result = await response.json();

      if (!response.ok || result.error) {
        throw new Error(result.error || 'Erreur lors de la restauration');
      }

      if (result.success) {
        setSuccessMessage(`Restauration réussie ! ${result.restored} tables restaurées.`);
      } else {
        setErrorMessage(`Restauration partielle : ${result.restored} tables restaurées, ${result.failed} échecs. ${result.errors?.join('; ') || ''}`);
      }
      setTimeout(() => { setSuccessMessage(''); setErrorMessage(''); }, 8000);
    } catch (error: any) {
      setErrorMessage(error.message || 'Erreur lors de la restauration');
      setTimeout(() => setErrorMessage(''), 8000);
    } finally {
      setBackupLoading(false);
      setRestoreTarget('');
    }
  };

  const handleDeleteBackup = async (fileName: string) => {
    if (!confirm(`Supprimer définitivement la sauvegarde ${fileName} ?`)) return;

    setBackupLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Non authentifié');

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/database-backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'delete', fileName }),
      });

      const result = await response.json();

      if (!response.ok || result.error) {
        throw new Error(result.error || 'Erreur lors de la suppression');
      }

      setSuccessMessage(`Sauvegarde ${fileName} supprimée`);
      setTimeout(() => setSuccessMessage(''), 3000);
      setBackups(backups.filter((b: any) => b.name !== fileName));
    } catch (error: any) {
      setErrorMessage(error.message || 'Erreur lors de la suppression');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setBackupLoading(false);
    }
  };

  const formatBackupDate = (name: string) => {
    const match = name.match(/backup-(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})/);
    if (!match) return name;
    const [, date, h, m, s] = match;
    return `${date} à ${h}:${m}:${s}`;
  };

  // === Visioconférence ===
  const loadVisioPermissions = async () => {
    setVisioLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'list-permissions' },
      });
      if (!error && data?.permissions) {
        setVisioPermissions(data.permissions);
      }
    } catch {
      // Silencieux
    } finally {
      setVisioLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!visioSearchTerm.trim() || visioSearchTerm.trim().length < 2) {
      setVisioSearchResults([]);
      return;
    }

    try {
      // Chercher dans toutes les tables de personnel (sauf patients)
      const term = visioSearchTerm.trim().toLowerCase();

      const tables = ['medecins', 'secretaires', 'laborantins', 'sage_femmes', 'infirmiers', 'super_admins'];
      const results: any[] = [];

      for (const table of tables) {
        const { data } = await supabase
          .from(table)
          .select('user_id, nom, prenom')
          .or(`nom.ilike.%${term}%,prenom.ilike.%${term}%`)
          .limit(10);

        if (data) {
          for (const row of data) {
            const alreadyHasPerm = visioPermissions.some((p: any) => p.user_id === row.user_id);
            if (!alreadyHasPerm) {
              results.push({
                user_id: row.user_id,
                nom: row.nom,
                prenom: row.prenom,
                source: table,
              });
            }
          }
        }
      }

      // Dédupliquer
      const seen = new Set();
      setVisioSearchResults(results.filter((r: any) => {
        const key = r.user_id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }));
    } catch {
      setVisioSearchResults([]);
    }
  };

  const grantPermission = async (targetUserId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'manage-permission', targetUserId, grant: true },
      });
      if (!error && data?.success) {
        setSuccessMessage('Permission accordée avec succès');
        setTimeout(() => setSuccessMessage(''), 3000);
        loadVisioPermissions();
        setVisioSearchResults(prev => prev.filter((r: any) => r.user_id !== targetUserId));
      }
    } catch {
      setErrorMessage('Erreur lors de l\'attribution de la permission');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const revokePermission = async (targetUserId: string) => {
    if (!confirm('Révoquer la permission de créer des réunions pour cet utilisateur ?')) return;
    try {
      const { data, error } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'manage-permission', targetUserId, grant: false },
      });
      if (!error && data?.success) {
        setSuccessMessage('Permission révoquée');
        setTimeout(() => setSuccessMessage(''), 3000);
        loadVisioPermissions();
      }
    } catch {
      setErrorMessage('Erreur lors de la révocation');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  useEffect(() => {
    if (activeTab === 'visio' && !loading) {
      loadVisioPermissions();
    }
  }, [activeTab, loading]);

  // === Vérification forcée par utilisateur ===
  const loadForcedUsersDetails = async (userIds: string[]) => {
    if (!userIds || userIds.length === 0) {
      setVerificationForcedUsers([]);
      return;
    }
    try {
      const users: any[] = [];
      const tables = ['medecins', 'secretaires', 'laborantins', 'sage_femmes', 'infirmiers', 'super_admins', 'patients'];
      for (const table of tables) {
        const { data } = await supabase
          .from(table)
          .select('user_id, nom, prenom')
          .in('user_id', userIds);
        if (data) {
          for (const row of data) {
            if (!users.find((u: any) => u.user_id === row.user_id)) {
              users.push({
                user_id: row.user_id,
                nom: row.nom,
                prenom: row.prenom,
                source: table,
              });
            }
          }
        }
      }
      // Récupérer les emails via auth admin
      for (const u of users) {
        try {
          if (supabase.auth.admin) {
            const { data: authData } = await supabase.auth.admin.getUserById(u.user_id);
            if (authData?.user?.email) u.email = authData.user.email;
          }
        } catch { /* ignore */ }
      }
      setVerificationForcedUsers(users);
    } catch {
      // Silencieux
    }
  };

  const searchUsersForVerification = async () => {
    if (!verificationSearchTerm.trim() || verificationSearchTerm.trim().length < 2) {
      setVerificationSearchResults([]);
      return;
    }
    setVerificationSearchLoading(true);
    try {
      const term = verificationSearchTerm.trim().toLowerCase();
      const tables = ['medecins', 'secretaires', 'laborantins', 'sage_femmes', 'infirmiers', 'super_admins', 'patients'];
      const results: any[] = [];

      for (const table of tables) {
        const { data } = await supabase
          .from(table)
          .select('user_id, nom, prenom')
          .or(`nom.ilike.%${term}%,prenom.ilike.%${term}%`)
          .limit(10);

        if (data) {
          for (const row of data) {
            const alreadyForced = verificationForcedUsers.some((u: any) => u.user_id === row.user_id);
            if (!alreadyForced) {
              results.push({
                user_id: row.user_id,
                nom: row.nom,
                prenom: row.prenom,
                source: table,
              });
            }
          }
        }
      }

      const seen = new Set();
      setVerificationSearchResults(results.filter((r: any) => {
        const key = r.user_id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }));
    } catch {
      setVerificationSearchResults([]);
    } finally {
      setVerificationSearchLoading(false);
    }
  };

  const addForcedVerification = (userId: string, nom: string, prenom: string, source: string) => {
    const newIds = [...securityConfig.verification_forced_user_ids, userId];
    const newUsers = [...verificationForcedUsers, { user_id: userId, nom, prenom, source }];
    setSecurityConfig({ ...securityConfig, verification_forced_user_ids: newIds });
    setVerificationForcedUsers(newUsers);
    setVerificationSearchResults(prev => prev.filter((r: any) => r.user_id !== userId));
  };

  const removeForcedVerification = (userId: string) => {
    const newIds = securityConfig.verification_forced_user_ids.filter((id: string) => id !== userId);
    const newUsers = verificationForcedUsers.filter((u: any) => u.user_id !== userId);
    setSecurityConfig({ ...securityConfig, verification_forced_user_ids: newIds });
    setVerificationForcedUsers(newUsers);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-clinic-blue-dark text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-clinic-blue-dark">Configuration Système</h1>
              <p className="text-gray-600 mt-2 text-sm sm:text-base">Paramètres globaux et maintenance de la plateforme</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 text-clinic-blue hover:text-clinic-blue-dark transition-colors whitespace-nowrap text-sm sm:text-base"
            >
              ← Retour au tableau de bord
            </button>
          </div>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 text-green-600 rounded-2xl flex items-start text-sm">
            <i className="ri-check-line text-xl mr-2 mt-0.5 flex-shrink-0"></i>
            <span>{successMessage}</span>
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl flex items-start text-sm">
            <i className="ri-error-warning-line text-xl mr-2 mt-0.5 flex-shrink-0"></i>
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-2 flex flex-col sm:flex-row gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('general')}
              className={`flex-1 px-4 sm:px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'general'
                  ? 'bg-clinic-blue text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-settings-3-line mr-2"></i>
              Général
            </button>
            <button
              onClick={() => setActiveTab('rdv')}
              className={`flex-1 px-4 sm:px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'rdv'
                  ? 'bg-clinic-blue text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-calendar-line mr-2"></i>
              Rendez-vous
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`flex-1 px-4 sm:px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'security'
                  ? 'bg-clinic-blue text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-shield-check-line mr-2"></i>
              Sécurité
            </button>
            <button
              onClick={() => setActiveTab('maintenance')}
              className={`flex-1 px-4 sm:px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'maintenance'
                  ? 'bg-clinic-blue text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-tools-line mr-2"></i>
              Maintenance
            </button>
            <button
              onClick={() => setActiveTab('visio')}
              className={`flex-1 px-4 sm:px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'visio'
                  ? 'bg-clinic-blue text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-vidicon-line mr-2"></i>
              Visioconférence
            </button>
          </div>
        </div>

        {/* Configuration Générale */}
        {activeTab === 'general' && (
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-gray-100">
            <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark mb-6">Configuration Générale</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la clinique</label>
                <input
                  type="text"
                  value={generalConfig.clinicName}
                  onChange={(e) => setGeneralConfig({ ...generalConfig, clinicName: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email de contact</label>
                  <input
                    type="email"
                    value={generalConfig.clinicEmail}
                    onChange={(e) => setGeneralConfig({ ...generalConfig, clinicEmail: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={generalConfig.clinicPhone}
                    onChange={(e) => setGeneralConfig({ ...generalConfig, clinicPhone: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                <input
                  type="text"
                  value={generalConfig.clinicAddress}
                  onChange={(e) => setGeneralConfig({ ...generalConfig, clinicAddress: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Horaires d'ouverture</label>
                  <input
                    type="text"
                    value={generalConfig.workingHours}
                    onChange={(e) => setGeneralConfig({ ...generalConfig, workingHours: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Jours d'ouverture</label>
                  <input
                    type="text"
                    value={generalConfig.workingDays}
                    onChange={(e) => setGeneralConfig({ ...generalConfig, workingDays: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
              </div>

              <button
                onClick={handleSaveGeneral}
                className="w-full sm:w-auto px-8 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap text-sm sm:text-base"
              >
                <i className="ri-save-line mr-2"></i>
                Enregistrer les modifications
              </button>
            </div>
          </div>
        )}

        {/* Configuration Rendez-vous */}
        {activeTab === 'rdv' && (
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-gray-100">
            <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark mb-6">Configuration des Rendez-vous</h2>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Durée consultation (minutes)</label>
                  <input
                    type="number"
                    value={rdvConfig.dureeConsultation}
                    onChange={(e) => setRdvConfig({ ...rdvConfig, dureeConsultation: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Délai annulation (heures)</label>
                  <input
                    type="number"
                    value={rdvConfig.delaiAnnulation}
                    onChange={(e) => setRdvConfig({ ...rdvConfig, delaiAnnulation: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max RDV par jour</label>
                  <input
                    type="number"
                    value={rdvConfig.maxRdvParJour}
                    onChange={(e) => setRdvConfig({ ...rdvConfig, maxRdvParJour: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rappel avant (heures)</label>
                  <input
                    type="number"
                    value={rdvConfig.rappelAvant}
                    onChange={(e) => setRdvConfig({ ...rdvConfig, rappelAvant: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
              </div>

              <div className="p-5 bg-orange-50 border-2 border-orange-200 rounded-2xl">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-bold text-orange-900 text-base mb-1">
                      <i className="ri-flashlight-fill mr-2 text-orange-600"></i>
                      Rendez-vous urgents
                    </h3>
                    <p className="text-sm text-orange-700">
                      Permet aux secrétaires de créer des rendez-vous urgents pour les médecins, 
                      même en dehors de leurs jours de travail habituels. Le médecin sera notifié 
                      avec la mention URGENT.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={rdvConfig.rendez_vous_urgents_active}
                      onChange={(e) => setRdvConfig({ ...rdvConfig, rendez_vous_urgents_active: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>
              </div>

              <button
                onClick={handleSaveRdv}
                className="w-full sm:w-auto px-8 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap text-sm sm:text-base"
              >
                <i className="ri-save-line mr-2"></i>
                Enregistrer les modifications
              </button>
            </div>
          </div>
        )}

        {/* Configuration Sécurité */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark mb-6">Configuration de Sécurité</h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timeout session (minutes)</label>
                    <input
                      type="number"
                      value={securityConfig.sessionTimeout}
                      onChange={(e) => setSecurityConfig({ ...securityConfig, sessionTimeout: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Max tentatives connexion</label>
                    <input
                      type="number"
                      value={securityConfig.maxLoginAttempts}
                      onChange={(e) => setSecurityConfig({ ...securityConfig, maxLoginAttempts: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Longueur minimale mot de passe</label>
                  <input
                    type="number"
                    value={securityConfig.passwordMinLength}
                    onChange={(e) => setSecurityConfig({ ...securityConfig, passwordMinLength: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="strongPassword"
                    checked={securityConfig.requireStrongPassword}
                    onChange={(e) => setSecurityConfig({ ...securityConfig, requireStrongPassword: e.target.checked })}
                    className="w-5 h-5 text-clinic-blue border-gray-300 rounded focus:ring-clinic-blue"
                  />
                  <label htmlFor="strongPassword" className="text-sm font-medium text-gray-700">
                    Exiger un mot de passe fort (majuscules, minuscules, chiffres, caractères spéciaux)
                  </label>
                </div>

                {/* Vérification en deux étapes */}
                <div className="pt-4 border-t-2 border-gray-200">
                  <h3 className="text-base font-bold text-clinic-blue-dark mb-2 flex items-center gap-2">
                    <i className="ri-shield-check-line text-clinic-green"></i>
                    Vérification en deux étapes (2FA)
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Activez ou désactivez l'envoi de codes de vérification lors de la connexion. 
                    Si désactivé, les utilisateurs pourront se connecter directement avec leur email/mot de passe.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-800">Vérification par email</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {securityConfig.requireEmailVerification 
                            ? 'Un code à 6 chiffres sera envoyé par email après la connexion' 
                            : 'Connexion directe sans code de vérification'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={securityConfig.requireEmailVerification}
                          onChange={(e) => setSecurityConfig({ ...securityConfig, requireEmailVerification: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${securityConfig.requireEmailVerification ? 'bg-clinic-green' : 'bg-gray-300'}`}></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-800">Vérification par téléphone (SMS)</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {securityConfig.requirePhoneVerification 
                            ? 'Un code à 6 chiffres sera envoyé par SMS après la connexion' 
                            : 'Connexion directe sans code SMS de vérification'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={securityConfig.requirePhoneVerification}
                          onChange={(e) => setSecurityConfig({ ...securityConfig, requirePhoneVerification: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${securityConfig.requirePhoneVerification ? 'bg-clinic-green' : 'bg-gray-300'}`}></div>
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-xs text-amber-700 flex items-start gap-2">
                      <i className="ri-alert-line flex-shrink-0 mt-0.5"></i>
                      <span>Désactiver la vérification réduit la sécurité. Les appareils de confiance restent actifs, donc les utilisateurs sur des appareils déjà vérifiés ne recevront pas de code même si l'option est activée.</span>
                    </p>
                  </div>
                </div>

                {/* Liste des utilisateurs avec vérification forcée */}
                <div className="pt-4 border-t-2 border-gray-200">
                  <h3 className="text-base font-bold text-clinic-blue-dark mb-2 flex items-center gap-2">
                    <i className="ri-user-settings-line text-clinic-blue"></i>
                    Vérification forcée par utilisateur
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Même si la vérification globale est désactivée, ces utilisateurs devront toujours passer par un code de vérification.
                  </p>

                  {/* Recherche utilisateur */}
                  <div className="flex gap-3 mb-4">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={verificationSearchTerm}
                        onChange={(e) => setVerificationSearchTerm(e.target.value)}
                        placeholder="Rechercher par nom ou prénom..."
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue/40 transition-colors text-sm"
                        onKeyDown={(e) => { if (e.key === 'Enter') searchUsersForVerification(); }}
                      />
                      <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    </div>
                    <button
                      onClick={searchUsersForVerification}
                      disabled={verificationSearchLoading}
                      className="px-4 py-2.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue/90 transition-colors font-semibold text-sm whitespace-nowrap disabled:opacity-50 cursor-pointer"
                    >
                      {verificationSearchLoading ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <><i className="ri-search-line mr-1.5"></i>Rechercher</>
                      )}
                    </button>
                  </div>

                  {/* Résultats de recherche */}
                  {verificationSearchResults.length > 0 && (
                    <div className="space-y-2 bg-blue-50 rounded-xl p-4 border border-blue-100 mb-4">
                      <p className="text-xs text-blue-700 font-medium mb-3">
                        <i className="ri-information-line mr-1"></i>
                        {verificationSearchResults.length} résultat(s) trouvé(s)
                      </p>
                      {verificationSearchResults.map((user: any) => (
                        <div key={user.user_id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-blue-100">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-clinic-blue/10 rounded-lg flex items-center justify-center">
                              <i className="ri-user-line text-clinic-blue"></i>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">{user.prenom} {user.nom}</p>
                              <p className="text-xs text-gray-500 capitalize">{user.source.replace(/_/g, ' ')}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => addForcedVerification(user.user_id, user.nom, user.prenom, user.source)}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-shield-flash-line mr-1"></i>Forcer vérification
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {verificationSearchTerm.length >= 2 && !verificationSearchLoading && verificationSearchResults.length === 0 && (
                    <div className="text-center py-4 bg-gray-50 rounded-xl mb-4">
                      <p className="text-sm text-gray-500">Aucun utilisateur trouvé ou déjà dans la liste</p>
                    </div>
                  )}

                  {/* Liste des utilisateurs avec vérification forcée */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <i className="ri-shield-check-line text-orange-500"></i>
                      Utilisateurs avec vérification forcée ({verificationForcedUsers.length})
                    </p>
                    {verificationForcedUsers.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <i className="ri-user-line text-gray-400 text-lg"></i>
                        </div>
                        <p className="text-sm text-gray-500 mb-1">Aucun utilisateur forcé</p>
                        <p className="text-xs text-gray-400">
                          {securityConfig.requireEmailVerification || securityConfig.requirePhoneVerification
                            ? 'La vérification est déjà active pour tout le monde'
                            : 'Utilisez la recherche ci-dessus pour forcer la vérification sur certains utilisateurs'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {verificationForcedUsers.map((user: any) => (
                          <div key={user.user_id} className="flex items-center justify-between p-3 bg-orange-50 rounded-xl border border-orange-200">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
                                <i className="ri-shield-flash-line text-orange-600"></i>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800">{user.prenom} {user.nom}</p>
                                <p className="text-xs text-gray-500 capitalize">{user.source?.replace(/_/g, ' ')}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => removeForcedVerification(user.user_id)}
                              className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-close-line mr-1"></i>Retirer
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleSaveSecurity}
                  className="w-full sm:w-auto px-8 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap text-sm sm:text-base"
                >
                  <i className="ri-save-line mr-2"></i>
                  Enregistrer les modifications
                </button>
              </div>
            </div>

            {/* Diagnostic Turnstile */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark mb-2">
                <i className="ri-shield-flash-line mr-2 text-amber-500"></i>
                Diagnostic Turnstile
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Vérifie la configuration du widget Turnstile (sitekey <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">0x4AAAAAACFuWJkMdzmwzN09</code>) 
                directement depuis l&apos;API Cloudflare. Le secret n&apos;est jamais exposé.
              </p>

              <button
                onClick={() => handleCheckTurnstile(cloudflareAccountId)}
                disabled={turnstileChecking}
                className="w-full sm:w-auto px-6 py-3 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-all font-semibold whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {turnstileChecking ? (
                  <><i className="ri-loader-4-line animate-spin mr-2"></i>Vérification en cours...</>
                ) : (
                  <><i className="ri-search-line mr-2"></i>Vérifier le widget Turnstile</>
                )}
              </button>

              {turnstileCheckResult && (
                <div className="mt-6">
                  {turnstileCheckResult.status === 'ok' ? (
                    <div className="space-y-4">
                      {/* Succès */}
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-lg text-green-600"></i>
                        </div>
                        <div>
                          <h3 className="font-semibold text-green-800 text-sm">Widget Turnstile connecté</h3>
                          <p className="text-xs text-green-700 mt-0.5">
                            Compte Cloudflare : <strong>{turnstileCheckResult.account?.name}</strong> ({turnstileCheckResult.account?.id})
                          </p>
                        </div>
                      </div>

                      {/* Détails widget */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <p className="text-xs text-gray-500 mb-1">Nom du widget</p>
                          <p className="text-sm font-semibold text-gray-800">{turnstileCheckResult.widget?.name || 'N/A'}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <p className="text-xs text-gray-500 mb-1">Mode</p>
                          <p className="text-sm font-semibold text-gray-800 capitalize">{turnstileCheckResult.widget?.mode || 'N/A'}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <p className="text-xs text-gray-500 mb-1">Clearance Level</p>
                          <p className="text-sm font-semibold text-gray-800">{turnstileCheckResult.widget?.clearance_level || 'N/A'}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <p className="text-xs text-gray-500 mb-1">Secret récupéré</p>
                          <p className="text-sm font-semibold text-gray-800">
                            {turnstileCheckResult.secret_retrieved ? (
                              <span className="text-green-600"><i className="ri-check-line mr-1"></i>Oui</span>
                            ) : (
                              <span className="text-red-600"><i className="ri-close-line mr-1"></i>Non</span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Domaines */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-xs text-gray-500 mb-2">Domaines enregistrés</p>
                        <div className="flex flex-wrap gap-2">
                          {(turnstileCheckResult.widget?.domains || []).map((domain: string) => (
                            <span key={domain} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              {domain}
                            </span>
                          ))}
                          {(turnstileCheckResult.widget?.domains || []).length === 0 && (
                            <span className="text-xs text-gray-500">Aucun domaine</span>
                          )}
                        </div>
                        {['www.cliniquesaintefamille.com', 'cliniquesaintefamille.vercel.app', 'cliniquesaintefamille.netlify.app', 'localhost'].some((d) => !(turnstileCheckResult.widget?.domains || []).includes(d)) && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                            <p className="text-xs text-amber-700 flex items-center gap-1 mb-2">
                              <i className="ri-alert-line"></i>
                              Certains domaines (www.cliniquesaintefamille.com, Vercel, Netlify) manquent — la connexion peut échouer sur ces adresses
                            </p>
                            <button
                              onClick={handleAddLocalhost}
                              disabled={addingLocalhost}
                              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all text-xs font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            >
                              {addingLocalhost ? (
                                <><i className="ri-loader-4-line animate-spin mr-1"></i>Ajout en cours...</>
                              ) : (
                                <><i className="ri-add-line mr-1"></i>Ajouter les domaines manquants</>
                              )}
                            </button>
                            {localhostAddResult && (
                              <div className="mt-2">
                                {localhostAddResult.status === 'ok' ? (
                                  <p className="text-xs text-green-600 flex items-center gap-1">
                                    <i className="ri-check-line"></i>
                                    {localhostAddResult.message || 'localhost ajout\u00e9 avec succ\u00e8s'}
                                  </p>
                                ) : (
                                  <p className="text-xs text-red-600 flex items-center gap-1">
                                    <i className="ri-error-warning-line"></i>
                                    {localhostAddResult.reason || 'Erreur lors de l\'ajout'}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : turnstileCheckResult.status === 'needs_account_id' ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-settings-line text-lg text-amber-600"></i>
                        </div>
                        <div>
                          <h3 className="font-semibold text-amber-800 text-sm">Account ID requis</h3>
                          <p className="text-xs text-amber-700 mt-1">{turnstileCheckResult.reason}</p>
                          <p className="text-xs text-amber-600 mt-2">{turnstileCheckResult.hint}</p>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="text"
                          value={cloudflareAccountId}
                          onChange={(e) => setCloudflareAccountId(e.target.value)}
                          placeholder="Collez votre Account ID ici (32 caractères hex)"
                          className="flex-1 px-4 py-3 border-2 border-amber-300 rounded-xl focus:outline-none focus:border-amber-500 text-sm font-mono"
                        />
                        <button
                          onClick={() => handleCheckTurnstile(cloudflareAccountId)}
                          disabled={turnstileChecking || !cloudflareAccountId.trim()}
                          className="w-full sm:w-auto px-6 py-3 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-all font-semibold whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                          {turnstileChecking ? (
                            <><i className="ri-loader-4-line animate-spin mr-2"></i>Vérification...</>
                          ) : (
                            <><i className="ri-search-line mr-2"></i>Vérifier avec cet Account ID</>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : turnstileCheckResult.status === 'multiple_accounts' ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <h3 className="font-semibold text-amber-800 text-sm mb-2">Plusieurs comptes Cloudflare trouvés</h3>
                      <div className="space-y-2">
                        {(turnstileCheckResult.accounts || []).map((acct: any) => (
                          <div key={acct.id} className="p-3 bg-white rounded-lg border border-amber-100 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-800">{acct.name}</p>
                              <p className="text-xs text-gray-500 font-mono">{acct.id}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : turnstileCheckResult.status === 'missing_read_scope' ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-key-2-line text-lg text-red-600"></i>
                        </div>
                        <div>
                          <h3 className="font-semibold text-red-800 text-sm">Permission manquante</h3>
                          <p className="text-xs text-red-700 mt-1">{turnstileCheckResult.reason}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-error-warning-line text-lg text-red-600"></i>
                        </div>
                        <div>
                          <h3 className="font-semibold text-red-800 text-sm">Erreur</h3>
                          <p className="text-xs text-red-700 mt-1">{turnstileCheckResult.reason || 'Erreur inconnue'}</p>
                          {turnstileCheckResult.detail && (
                            <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto">
                              {JSON.stringify(turnstileCheckResult.detail, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Maintenance */}
        {activeTab === 'maintenance' && (
          <div className="space-y-6">
            {/* Mode Maintenance */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark mb-6">Mode Maintenance</h2>
              
              {maintenanceActive ? (
                <div className="space-y-4">
                  <div className="p-4 sm:p-6 bg-orange-50 border-2 border-orange-200 rounded-xl">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-2xl text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg font-bold text-orange-800">Mode maintenance actif</h3>
                        <p className="text-sm text-orange-600">Le site est actuellement en maintenance</p>
                      </div>
                    </div>
                    <button
                      onClick={handleDeactivateMaintenance}
                      className="w-full px-6 py-3 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap text-sm sm:text-base"
                    >
                      <i className="ri-close-circle-line mr-2"></i>
                      Désactiver la maintenance
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="p-4 sm:p-6 bg-blue-50 border-2 border-blue-200 rounded-xl">
                    <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-information-line text-2xl text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg font-bold text-blue-800 mb-2">À propos du mode maintenance</h3>
                        <p className="text-sm text-blue-700 leading-relaxed">
                          Le mode maintenance bloque l'accès au site pour tous les utilisateurs sauf les administrateurs. 
                          Vous pouvez choisir une durée déterminée ou une maintenance illimitée sans compteur.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Toggle maintenance illimitée */}
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={maintenanceIllimitee}
                        onChange={(e) => setMaintenanceIllimitee(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                    </label>
                    <div>
                      <span className="text-sm font-semibold text-gray-800">Maintenance illimitée</span>
                      <p className="text-xs text-gray-500 mt-0.5">Aucun compteur ne sera affiché, la maintenance restera active jusqu'à désactivation manuelle</p>
                    </div>
                  </div>

                  {!maintenanceIllimitee && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Durée de la maintenance (heures)
                      </label>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                        <input
                          type="range"
                          min="1"
                          max="24"
                          value={maintenanceDuration}
                          onChange={(e) => setMaintenanceDuration(parseInt(e.target.value))}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${(maintenanceDuration / 24) * 100}%, #e5e7eb ${(maintenanceDuration / 24) * 100}%, #e5e7eb 100%)`
                          }}
                        />
                        <div className="w-full sm:w-20 px-4 py-2 bg-gray-100 rounded-xl text-center font-bold text-clinic-blue-dark">
                          {maintenanceDuration}h
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>1 heure</span>
                        <span>24 heures</span>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleActivateMaintenance}
                    className="w-full px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full hover:from-orange-600 hover:to-orange-700 transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap text-sm sm:text-base"
                  >
                    <i className="ri-tools-line mr-2"></i>
                    Activer le mode maintenance{maintenanceIllimitee ? ' (illimitée)' : ` (${maintenanceDuration}h)`}
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark mb-6">Sauvegarde &amp; Restauration</h2>

              {/* Auto-backup status */}
              <div className={`mb-6 p-4 sm:p-5 rounded-xl border-2 transition-all ${backupCronStatus.checking ? 'bg-gray-50 border-gray-200' : backupCronStatus.configured ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-200'}`}>
                {backupCronStatus.checking ? (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 animate-pulse">
                      <i className="ri-loader-4-line animate-spin text-lg text-gray-500"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-700 text-sm">Vérification du cron de sauvegarde...</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Connexion au serveur en cours</p>
                    </div>
                  </div>
                ) : backupCronStatus.configured ? (
                  <div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-double-line text-xl text-green-600"></i>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-green-800 text-sm">Sauvegarde automatique active</h3>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-200 text-green-800 rounded-full text-xs font-medium">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                              En service
                            </span>
                          </div>
                          <p className="text-xs text-green-700 mt-1">
                            Backup programmé chaque jour à <strong>minuit (00h00 UTC)</strong>
                            {backupCronStatus.activated_at && (
                              <> — Activé le {new Date(backupCronStatus.activated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                            )}
                          </p>
                          {backupCronStatus.cron_job_exists && (
                            <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1">
                              <i className="ri-database-2-line"></i>
                              Job pg_cron &quot;daily-database-backup&quot; confirmé sur le serveur
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={checkBackupCronStatus}
                          className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                          disabled={backupCronStatus.checking}
                        >
                          <i className="ri-refresh-line mr-1"></i>Vérifier
                        </button>
                        <button
                          onClick={handleSetupBackupCron}
                          disabled={backupLoading}
                          className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                          {backupLoading ? (
                            <><i className="ri-loader-4-line animate-spin mr-1.5"></i>...</>
                          ) : (
                            <><i className="ri-refresh-line mr-1.5"></i>Reconfigurer</>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="ri-timer-line text-xl text-amber-600"></i>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-amber-800 text-sm">Sauvegarde automatique non configurée</h3>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-xs font-medium">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                              Inactif
                            </span>
                          </div>
                          <p className="text-xs text-amber-700 mt-1">
                            Aucun cron job détecté. Les sauvegardes ne se feront pas automatiquement.
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            Cliquez sur le bouton ci-contre pour activer la sauvegarde quotidienne à minuit.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleSetupBackupCron}
                        disabled={backupLoading}
                        className="w-full sm:w-auto px-5 py-2.5 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-all font-semibold whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {backupLoading ? (
                          <><i className="ri-loader-4-line animate-spin mr-1.5"></i>Configuration...</>
                        ) : (
                          <><i className="ri-timer-flash-line mr-1.5"></i>Activer la sauvegarde auto</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Manual backup */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-blue-50 rounded-xl gap-4 mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-clinic-blue-dark text-sm sm:text-base">Sauvegarde manuelle</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Créer une sauvegarde complète immédiate de toutes les données</p>
                </div>
                <button
                  onClick={handleBackup}
                  disabled={backupLoading}
                  className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {backupLoading ? (
                    <><i className="ri-loader-4-line animate-spin mr-2"></i>Sauvegarde...</>
                  ) : (
                    <><i className="ri-download-cloud-line mr-2"></i>Sauvegarder maintenant</>
                  )}
                </button>
              </div>

              {/* Restore section */}
              <div className="p-4 bg-green-50 rounded-xl mb-4">
                <h3 className="font-semibold text-clinic-blue-dark text-sm sm:text-base mb-3">Restaurer une sauvegarde</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-3">
                  Sélectionnez un fichier de sauvegarde ci-dessous puis cliquez sur Restaurer.
                  <strong className="text-red-600"> Attention : toutes les données actuelles seront remplacées.</strong>
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <select
                    value={restoreTarget}
                    onChange={(e) => setRestoreTarget(e.target.value)}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 text-sm bg-white"
                  >
                    <option value="">-- Choisir une sauvegarde --</option>
                    {backups.map((b: any) => (
                      <option key={b.name} value={b.name}>{formatBackupDate(b.name)}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleRestoreConfirm}
                    disabled={backupLoading || !restoreTarget}
                    className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-all font-semibold whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-upload-cloud-line mr-2"></i>
                    Restaurer
                  </button>
                </div>
              </div>

              {/* Backups list */}
              <div>
                <h3 className="font-semibold text-clinic-blue-dark text-sm sm:text-base mb-3">
                  Sauvegardes disponibles ({backups.length})
                </h3>
                {backups.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <i className="ri-folder-open-line text-4xl block mb-2"></i>
                    <p className="text-sm">Aucune sauvegarde pour le moment</p>
                    <p className="text-xs mt-1">Cliquez sur "Sauvegarder maintenant" pour créer la première</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {backups.map((b: any) => (
                      <div key={b.name} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-gray-50 rounded-xl gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-file-code-line text-blue-600"></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{formatBackupDate(b.name)}</p>
                            <p className="text-xs text-gray-500">{b.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => { setRestoreTarget(b.name); }}
                            className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-refresh-line mr-1"></i>Sélectionner
                          </button>
                          <button
                            onClick={() => handleDeleteBackup(b.name)}
                            disabled={backupLoading}
                            className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                          >
                            <i className="ri-delete-bin-line mr-1"></i>Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark mb-6">Nettoyage &amp; Optimisation</h2>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-yellow-50 rounded-xl gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-clinic-blue-dark text-sm sm:text-base">Nettoyer les logs anciens</h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Supprimer les logs de plus de 90 jours</p>
                  </div>
                  <button className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-yellow-600 text-white rounded-full hover:bg-yellow-700 transition-all font-semibold whitespace-nowrap text-sm">
                    <i className="ri-delete-bin-line mr-2"></i>
                    Nettoyer
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-purple-50 rounded-xl gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-clinic-blue-dark text-sm sm:text-base">Optimiser la base de données</h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Réorganiser et optimiser les tables</p>
                  </div>
                  <button className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-all font-semibold whitespace-nowrap text-sm">
                    <i className="ri-speed-up-line mr-2"></i>
                    Optimiser
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-red-100">
              <h2 className="text-xl sm:text-2xl font-bold text-red-600 mb-6">Zone Dangereuse</h2>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-red-50 rounded-xl gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-700 text-sm sm:text-base">Réinitialiser toutes les données</h3>
                    <p className="text-xs sm:text-sm text-red-600 mt-1">⚠️ Action irréversible - Supprime toutes les données</p>
                  </div>
                  <button className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-all font-semibold whitespace-nowrap text-sm">
                    <i className="ri-alert-line mr-2"></i>
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Visioconférence */}
        {activeTab === 'visio' && (
          <div className="space-y-6">
            {/* Gestion des permissions */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark mb-2">Permissions de création de réunions</h2>
              <p className="text-sm text-gray-600 mb-6">
                Gérez qui peut créer des réunions de visioconférence. Les utilisateurs autorisés pourront générer des codes de réunion à partager.
              </p>

              {/* Utilisateurs avec permission */}
              <div className="mb-8">
                <h3 className="text-base font-semibold text-clinic-blue-dark mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line text-clinic-green"></i>
                  Utilisateurs autorisés ({visioPermissions.length})
                </h3>
                {visioLoading ? (
                  <div className="text-center py-6">
                    <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
                  </div>
                ) : visioPermissions.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-user-line text-gray-300 text-2xl"></i>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Aucun utilisateur autorisé</p>
                    <p className="text-xs text-gray-500">Utilisez la recherche ci-dessous pour ajouter des utilisateurs</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {visioPermissions.map((perm: any) => (
                      <div key={perm.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-clinic-green/10 rounded-xl flex items-center justify-center">
                            <i className="ri-user-star-line text-clinic-green"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              {perm.user?.raw_user_meta_data?.prenom || 'Utilisateur'} {perm.user?.raw_user_meta_data?.nom || ''}
                            </p>
                            <p className="text-xs text-gray-500">{perm.user?.email || ''}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => revokePermission(perm.user_id)}
                          className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-close-line mr-1"></i>Révoquer
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rechercher et ajouter */}
              <div>
                <h3 className="text-base font-semibold text-clinic-blue-dark mb-4 flex items-center gap-2">
                  <i className="ri-user-add-line text-clinic-blue"></i>
                  Ajouter un utilisateur
                </h3>
                <div className="flex gap-3 mb-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={visioSearchTerm}
                      onChange={(e) => setVisioSearchTerm(e.target.value)}
                      placeholder="Rechercher par nom ou prénom..."
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue/40 transition-colors text-sm"
                      onKeyDown={(e) => { if (e.key === 'Enter') searchUsers(); }}
                    />
                    <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  </div>
                  <button
                    onClick={searchUsers}
                    className="px-5 py-3 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue/90 transition-colors font-semibold text-sm whitespace-nowrap"
                  >
                    <i className="ri-search-line mr-1.5"></i>
                    Rechercher
                  </button>
                </div>

                {/* Résultats de recherche */}
                {visioSearchResults.length > 0 && (
                  <div className="space-y-2 bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <p className="text-xs text-blue-700 font-medium mb-3">
                      <i className="ri-information-line mr-1"></i>
                      {visioSearchResults.length} résultat(s) trouvé(s)
                    </p>
                    {visioSearchResults.map((user: any) => (
                      <div key={user.user_id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-blue-100">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-clinic-blue/10 rounded-lg flex items-center justify-center">
                            <i className="ri-user-line text-clinic-blue"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{user.prenom} {user.nom}</p>
                            <p className="text-xs text-gray-500 capitalize">{user.source.replace('_', ' ')}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => grantPermission(user.user_id)}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-clinic-green rounded-lg hover:bg-clinic-green-dark transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-add-line mr-1"></i>Autoriser
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {visioSearchTerm.length >= 2 && visioSearchResults.length === 0 && (
                  <div className="text-center py-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-500">Aucun utilisateur trouvé ou tous ont déjà la permission</p>
                  </div>
                )}
              </div>
            </div>

            {/* Réunions actives */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8 border-2 border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-clinic-blue-dark mb-2">Réunions en cours</h2>
              <p className="text-sm text-gray-600 mb-6">
                Surveillez les réunions actives sur la plateforme.
              </p>
              <button
                onClick={() => navigate('/visioconference')}
                className="px-6 py-3 bg-clinic-green text-white rounded-xl hover:bg-clinic-green-dark transition-colors font-semibold text-sm whitespace-nowrap"
              >
                <i className="ri-external-link-line mr-2"></i>
                Ouvrir le lobby visioconférence
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmation restauration */}
      {showRestoreConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full border-2 border-red-200">
            <div className="text-center mb-4">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Confirmer la restauration</h3>
              <p className="text-sm text-gray-600 mt-2">
                Vous allez restaurer la sauvegarde <strong>{formatBackupDate(restoreTarget)}</strong>.
              </p>
              <div className="mt-3 p-3 bg-red-50 rounded-xl text-left">
                <p className="text-sm text-red-700 font-semibold mb-1">
                  <i className="ri-error-warning-line mr-1"></i>
                  Cette action est irréversible :
                </p>
                <ul className="text-xs text-red-600 space-y-1 list-disc list-inside">
                  <li>Toutes les données actuelles seront supprimées</li>
                  <li>Les données de la sauvegarde les remplaceront</li>
                  <li>Les utilisateurs connectés seront déconnectés</li>
                </ul>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowRestoreConfirm(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
              <button
                onClick={handleRestoreExecute}
                disabled={backupLoading}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-all font-semibold text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {backupLoading ? (
                  <><i className="ri-loader-4-line animate-spin mr-1"></i>Restauration...</>
                ) : (
                  <><i className="ri-upload-cloud-line mr-1"></i>Confirmer la restauration</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
